import { memo } from 'react';
import { spacing } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';
import PatternThumbnail from './PatternThumbnail';
import ASCIIPreview from './ASCIIPreview';
import brandIcon from '../../assets/ai-image-ascii.png';

/**
 * ChatMessageItem Component
 *
 * Renders an individual message (user or assistant).
 * For selected assistant messages, shows full interactive ASCII preview.
 * For non-selected, shows clickable thumbnail.
 */
const ChatMessageItem = memo(({
  message,
  isSelected,
  onSelect,
  colors,
  // ASCII Preview props (only used for selected message)
  asciiText,
  columns,
  isFullscreen,
  onFullscreenClose,
  onFreeze,
  onUnfreeze,
  isFrozen,
  backgroundColor,
  foregroundColor,
}) => {
  if (message.type === 'user') {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
      }}>
        <div style={{
          maxWidth: '70%',
          padding: `${spacing.md} ${spacing.lg}`,
          background: colors.foreground,
          color: colors.background,
          borderRadius: '18px',
          borderBottomRightRadius: '6px',
          fontSize: '0.9375rem',
          fontWeight: 450,
          lineHeight: 1.6,
          fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}>
          {message.prompt}
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div style={{
      display: 'flex',
      gap: spacing.md,
      alignItems: 'flex-start',
    }}>
      {/* AI Avatar */}
      <img
        src={brandIcon}
        alt="Significa"
        style={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          objectFit: 'contain',
          flexShrink: 0,
        }}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          fontSize: '0.8125rem',
          fontWeight: 500,
          color: colors.foreground,
        }}>
          Significa
        </span>

        {message.error ? (
          <div style={{
            marginTop: spacing.xs,
            padding: spacing.md,
            background: colors.backgroundOffset,
            borderRadius: '6px',
            color: colors.foregroundSecondary,
            fontSize: '0.875rem',
          }}>
            Failed to generate: {message.error}
          </div>
        ) : message.patternCode ? (
          isSelected && asciiText ? (
            // Full interactive ASCII preview for selected message
            <div style={{
              marginTop: spacing.sm,
              padding: spacing.md,
              background: colors.backgroundPanel,
              border: `2px solid #6D916C`,
              borderRadius: '8px',
              overflow: 'auto',
            }}>
              <ASCIIPreview
                asciiText={asciiText}
                columns={columns}
                isFullscreen={isFullscreen}
                onFullscreenClose={onFullscreenClose}
                onFreeze={onFreeze}
                onUnfreeze={onUnfreeze}
                isFrozen={isFrozen}
                backgroundColor={backgroundColor}
                foregroundColor={foregroundColor}
              />
            </div>
          ) : (
            // Thumbnail for non-selected messages
            <PatternThumbnail
              patternCode={message.patternCode}
              isSelected={isSelected}
              onClick={onSelect}
            />
          )
        ) : null}
      </div>
    </div>
  );
});

/**
 * ChatMessageList Component
 *
 * Renders the full conversation thread in a Claude-like format.
 * The selected message shows full ASCII preview, others show thumbnails.
 */
const ChatMessageList = ({
  messages,
  selectedMessageId,
  onSelectMessage,
  // ASCII Preview props for selected message
  asciiText,
  columns,
  isFullscreen,
  onFullscreenClose,
  onFreeze,
  onUnfreeze,
  isFrozen,
  backgroundColor,
  foregroundColor,
  isGenerating = false,
  pendingPrompt = null,
}) => {
  const { colors } = useTheme();

  // Check if pending prompt is already in messages
  const pendingAlreadyInMessages = pendingPrompt && messages.some(m => m.type === 'user' && m.prompt === pendingPrompt);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.lg,
      width: '100%',
    }}>
      {/* Show pending message if not yet in messages array */}
      {pendingPrompt && !pendingAlreadyInMessages && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <div style={{
            maxWidth: '70%',
            padding: `${spacing.md} ${spacing.lg}`,
            background: colors.foreground,
            color: colors.background,
            borderRadius: '18px',
            borderBottomRightRadius: '6px',
            fontSize: '0.9375rem',
            fontWeight: 450,
            lineHeight: 1.6,
            fontFamily: 'Sohne, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}>
            {pendingPrompt}
          </div>
        </div>
      )}

      {messages.map((message) => (
        <ChatMessageItem
          key={message.id}
          message={message}
          isSelected={message.id === selectedMessageId}
          onSelect={() => onSelectMessage(message.id)}
          colors={colors}
          // Pass ASCII preview props only to the selected message
          asciiText={message.id === selectedMessageId ? asciiText : null}
          columns={columns}
          isFullscreen={isFullscreen}
          onFullscreenClose={onFullscreenClose}
          onFreeze={onFreeze}
          onUnfreeze={onUnfreeze}
          isFrozen={isFrozen}
          backgroundColor={backgroundColor}
          foregroundColor={foregroundColor}
        />
      ))}

      {/* Generating indicator */}
      {isGenerating && (
        <div style={{
          display: 'flex',
          gap: spacing.md,
          alignItems: 'flex-start',
        }}>
          <img
            src={brandIcon}
            alt="Significa"
            style={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1 }}>
            <span style={{
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: colors.foreground,
            }}>
              Significa
            </span>
            <div style={{
              marginTop: spacing.sm,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
            }}>
              <style>{`
                @keyframes pulse-dot {
                  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
                  40% { opacity: 1; transform: scale(1); }
                }
              `}</style>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: colors.foregroundSecondary,
                    animation: `pulse-dot 1.4s ease-in-out ${i * 0.16}s infinite`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessageList;
